import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
} from "react";
import useSwr, { useSWRConfig } from "swr";
import { CURRENT_USER_KEY, fetchCurrentUser, LogOutApi } from "../swr/user";
import { useWalletDetails, WALLET_DETAILS_KEY } from "../swr/wallet";
import { UserInfo } from "../types/User";
import { UserTypeBackEnd } from "../types/UserType";
import SessionContext from "./SessionContext";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthContextProps {
  status: AuthStatus;
  user: UserInfo | null;
  isAuthenticated: boolean;
  roles: string[];
  // Re-fetches /api/Account/me, e.g. right after sign in.
  refreshUser: () => Promise<UserInfo | null>;
  // Ends the server session and clears locally cached user data.
  signOut: () => Promise<void>;
}

// Per-user values still cached in localStorage. "token" is the legacy JWT key,
// removed so stale tokens from before the cookie migration don't linger.
const USER_STORAGE_KEYS = ["paymentState", "token", "id", "email", "homezone_wallet"];

export const clearUserStorage = () => {
  USER_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const AuthContext = createContext<AuthContextProps>({
  status: "loading",
  user: null,
  isAuthenticated: false,
  roles: [],
  refreshUser: async () => null,
  signOut: async () => {},
});

// /api/Account/me doesn't return roles yet, so fall back to the account's
// userType ("Landlord" | "Agent" | "Tenant"), which route privileges also use.
const toRoles = (user: UserInfo | null): string[] => {
  if (!user) return [];
  if (user.roles) return Array.isArray(user.roles) ? user.roles : [user.roles];
  const userType =
    user.userType !== undefined ? UserTypeBackEnd[user.userType] : undefined;
  return userType ? [userType] : [];
};

// setTimeout overflows above ~24.8 days.
const MAX_TIMEOUT_MS = 2147483647;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { setIsExpired } = useContext(SessionContext);
  const { cache, mutate } = useSWRConfig();

  // data: undefined while loading, null when signed out.
  const { data, error } = useSwr(CURRENT_USER_KEY, fetchCurrentUser, {
    shouldRetryOnError: false,
  });

  const sessionUser = data?.data ?? null;

  // /me doesn't return wallet_id yet; the wallet's virtual_account_no is the
  // same value (it's what the API looks the wallet up by).
  const { walletDetails } = useWalletDetails(!!sessionUser);
  const user = React.useMemo(
    () =>
      sessionUser && {
        ...sessionUser,
        wallet_id: sessionUser.wallet_id || walletDetails?.virtual_account_no,
      },
    [sessionUser, walletDetails?.virtual_account_no],
  );

  const status: AuthStatus = user
    ? "authenticated"
    : data === null || error
      ? "anonymous"
      : "loading";

  useEffect(() => {
    localStorage.removeItem("token");
  }, []);

  // Pages restored from the back/forward cache keep their old React state, so
  // re-check the session instead of showing a stale signed-in page.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) mutate(CURRENT_USER_KEY);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [mutate]);

  const refreshUser = useCallback(async () => {
    const [result] = await Promise.all([
      mutate(CURRENT_USER_KEY),
      // No wallet yet is fine here; it shouldn't fail the session refresh.
      mutate(WALLET_DETAILS_KEY).catch(() => undefined),
    ]);
    return result?.data ?? null;
  }, [mutate]);

  const signOut = useCallback(async () => {
    await LogOutApi(); // a 401 means the session already ended
    clearUserStorage();
    (cache as Map<string, unknown>).clear();
    await mutate(CURRENT_USER_KEY, null, false);
  }, [cache, mutate]);

  // The cookie expires with the token and there is no refresh endpoint, so
  // drop the session locally when expiresAt passes.
  useEffect(() => {
    if (!user?.expiresAt) return;
    const msLeft = new Date(user.expiresAt).getTime() - Date.now();
    if (Number.isNaN(msLeft)) return;

    const expire = () => {
      clearUserStorage();
      mutate(CURRENT_USER_KEY, null, false);
      setIsExpired(true);
    };

    if (msLeft <= 0) {
      expire();
      return;
    }
    const timer = setTimeout(expire, Math.min(msLeft, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [user?.expiresAt, mutate, setIsExpired]);

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        isAuthenticated: status === "authenticated",
        roles: toRoles(user),
        refreshUser,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => useContext(AuthContext);

export default AuthContext;
